import { isRunningOnServer } from '@/utils/isRunningOnServer'
import { ReadonlyHeaders } from 'next/dist/server/web/spec-extension/adapters/headers'
import { userAgent } from 'next/server'

export enum DeviceType {
  DESKTOP = 'desktop',
  TABLET = 'tablet',
  MOBILE = 'mobile',
}

/**
 * Detects whether called server- or clientside
 */
export const isBrowser = () => !isServer()
export const isServer = () => isRunningOnServer()

export type Device = {
  model?: string
  type?: string
  vendor?: string
  os?: string
}

export const isMobile = (device: Device) => {
  return device.type === DeviceType.MOBILE || device.type === DeviceType.TABLET
}

export const isAndroid = (device: Device) => {
  return device.os?.toLowerCase() === 'android'
}

export const isIOS = (device: Device) => {
  return device.os?.toLowerCase() === 'ios'
}

/**
 * Detects the device based on the user agent headers.
 * @link https://nextjs.org/docs/app/api-reference/functions/user-agent
 *
 * @param headers
 *
 * @returns {Device} An instance of the Device class containing model, type, vendor, and OS information.
 */
export const device = (headers: ReadonlyHeaders): Device => {
  const { device, os } = userAgent({ headers })
  return {
    model: device.model,
    type: device.type,
    vendor: device.vendor,
    os: os.name,
  } as Device
}

export enum PageType {
  HOME = 'home',
  ARTICLE = 'article',
  CLIMATE_UPDATE = 'climate-update',
  ODS = 'ods',
  MAPS = 'maps',
  UNKNOWN = 'unknown',
}

/**
 * Determines the page type based on the title.
 * @param title
 *
 * @return {PageType} The type of the page.
 */
export const pageType = (title?: string): PageType => {
  switch (title?.toLowerCase()) {
    case 'home':
    case 'startseite':
    case 'frontpage':
      return PageType.HOME
    default:
      return PageType.UNKNOWN
  }
}

/**
 * Extracts the ad mode from the URL search parameters.
 * @param search
 *
 * @returns {string} The ad mode, or an undefied if not found.
 */
export const extractAdMode = (search?: URLSearchParams) => {
  if (search?.get('ada') === '1' || search?.get('gujAd') === '1') {
    return 'preview'
  }
  return search?.get('adMode') ?? undefined
}
